import {
  Fingerprint,
  Languages,
  Mic2,
  Route,
  UserRoundCheck,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { summarizeNarrationLocale } from "./narrationLocale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyHint } from "./EchoflicksSessionList";
import {
  formatLanguage,
  isDefaultLanguage,
  languageLabel,
} from "../contact-language/languages";
import {
  summarizeVoiceLanguage,
  summarizeVoiceSelection,
} from "./voiceSelection";

type EchoVoiceSelectionProps = {
  envelope: Record<string, unknown> | undefined;
  isNarrated?: boolean | null;
  /** Echo projection `content_language`; the envelope is the fallback. */
  contentLanguage?: string | null;
  loading?: boolean;
};

export function EchoVoiceSelection({
  envelope,
  isNarrated,
  contentLanguage,
  loading = false,
}: EchoVoiceSelectionProps) {
  const selection = summarizeVoiceSelection(envelope, isNarrated);
  const language = summarizeVoiceLanguage(envelope, contentLanguage);
  const locale = summarizeNarrationLocale(envelope);
  // Shown only when the narration is not the English default (or the composer
  // reports the TTS code it sent), so pre-feature Echoes render unchanged.
  const showLanguage =
    !isDefaultLanguage(language.contentLanguage) ||
    language.languageCodeSent !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>ElevenLabs voice</CardTitle>
        <p className="text-sm text-muted-foreground">
          The narration voice selected for this Echo and the evidence behind the
          director&apos;s choice.
        </p>
      </CardHeader>
      <CardContent>
        {selection ? (
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <Mic2 className="size-4" aria-hidden="true" />
              </div>
              <div className="min-w-0 space-y-2">
                <div>
                  <div className="font-medium">{selection.title}</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <Badge variant="secondary">{selection.providerLabel}</Badge>
                    <Badge variant="outline">{selection.sourceLabel}</Badge>
                    {selection.legacy ? (
                      <Badge variant="outline">Legacy Echo</Badge>
                    ) : null}
                  </div>
                </div>
                {selection.voiceId ? (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Fingerprint className="size-3.5" aria-hidden="true" />
                    Voice ID
                    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
                      {selection.voiceId}
                    </code>
                  </div>
                ) : null}
              </div>
            </div>

            {showLanguage ? (
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Languages className="size-4" aria-hidden="true" />
                </div>
                <div className="min-w-0 space-y-1">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Narration language
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span>
                      {formatLanguage(languageLabel(language.contentLanguage))}
                    </span>
                    {language.languageCodeSent ? (
                      <Badge
                        variant="outline"
                        title="language_code the composer sent to the TTS model"
                      >
                        TTS code sent · {language.languageCodeSent}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {locale ? (
              <div className="space-y-1 text-sm">
                <div className="font-medium">Narration accent</div>
                <p>
                  Requested {locale.requested} · Effective {locale.effective}
                </p>
                <p className="text-muted-foreground">Source: {locale.source}</p>
                {locale.fallback ? <p>Fallback: {locale.fallback}</p> : null}
                <p className="text-muted-foreground">{locale.acousticStatus}</p>
              </div>
            ) : null}

            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <UserRoundCheck className="size-4" aria-hidden="true" />
              </div>
              <div className="min-w-0 space-y-1">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Why this voice
                </div>
                <p className="text-sm leading-relaxed">{selection.rationale}</p>
              </div>
            </div>

            {selection.requirements.length > 0 ||
            selection.profile.length > 0 ? (
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Decision evidence
                </div>
                <div className="flex flex-wrap gap-2">
                  {selection.requirements.map((requirement) => (
                    <Badge key={requirement} variant="secondary">
                      {requirement}
                    </Badge>
                  ))}
                  {selection.profile.map((trait) => (
                    <Badge key={`profile-${trait}`} variant="outline">
                      Voice profile · {trait}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}

            {selection.decisionPathLabel ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Route className="size-3.5" aria-hidden="true" />
                <span>{selection.decisionPathLabel}</span>
                {selection.candidateCount !== null ? (
                  <span>
                    · {selection.candidateCount} eligible catalog voice
                    {selection.candidateCount === 1 ? "" : "s"}
                  </span>
                ) : null}
              </div>
            ) : null}

            {selection.catalogReleaseId || selection.renderProfileId ? (
              <div className="grid gap-2 rounded-md border bg-muted/30 p-3 text-xs sm:grid-cols-2">
                <AuditValue
                  label="Frozen catalog release"
                  value={selection.catalogReleaseId}
                />
                <AuditValue
                  label="Render profile"
                  value={selection.renderProfileId}
                />
                <AuditValue
                  label="Catalog digest"
                  value={selection.catalogDigest}
                />
                <AuditValue
                  label="Render profile digest"
                  value={selection.renderProfileDigest}
                />
                {selection.seed !== null ? (
                  <AuditValue
                    label="Persisted seed"
                    value={String(selection.seed)}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : loading ? (
          <EmptyHint>Loading the voice decision...</EmptyHint>
        ) : (
          <EmptyHint>
            No voice-selection record was available for this Echo.
          </EmptyHint>
        )}
      </CardContent>
    </Card>
  );
}

function AuditValue({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground">{label}</div>
      <code
        className="mt-0.5 block truncate font-mono text-foreground"
        title={value}
      >
        {value}
      </code>
    </div>
  );
}
